import {
  getModelConfigList,
  getModelNames,
  getLlmModelCodeList,
  getModelProviderList,
  getModelConfig,
  addModelConfig,
  editModelConfig,
  deleteModelConfig,
  enableModelConfig,
  activateModelConfig,
  setDefaultModel,
  getModelVoices,
  getVoiceList,
  saveVoice,
  updateVoice,
  deleteVoice,
  getModelTypes,
  getModelQuota
} from '@/apis/model/model'

const silentError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Model]', ...args.map(a => a?.msg || a?.message || a))
  }
}

const state = {
  modelList: [],
  total: 0,
  loading: false,
  modelProviders: [],
  currentModel: null,
  modelVoices: [],
  modelTypes: []
}

const mutations = {
  SET_MODEL_LIST(state, { list, total }) {
    state.modelList = list
    state.total = total
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_MODEL_PROVIDERS(state, providers) {
    state.modelProviders = providers
  },
  SET_CURRENT_MODEL(state, model) {
    state.currentModel = model
  },
  SET_MODEL_VOICES(state, voices) {
    state.modelVoices = voices
  },
  SET_MODEL_TYPES(state, types) {
    state.modelTypes = types
  }
}

const actions = {
  async fetchModelTypes({ commit }) {
    try {
      const data = await getModelTypes()
      // 排除 PLUGIN 类型
      const filteredTypes = (data || []).filter(type => type.toUpperCase() !== 'PLUGIN')
      commit('SET_MODEL_TYPES', filteredTypes)
      return filteredTypes
    } catch (error) {
      silentError('获取模型类型失败:', error)
      throw error
    }
  },
  async fetchModelList({ commit }, params) {
    commit('SET_LOADING', true)
    try {
      // 转换参数以符合后端接口 (page 从 0 开始，limit 为每页条数)
      const query = {
        page: params.pageNum.toString(),
        limit: params.pageSize.toString(),
        modelType: params.modelType || 'LLM', // 必填字段，默认为 LLM
        modelName: params.name,
        isActivated: params.isActivated
      }
      const data = await getModelConfigList(query)
      commit('SET_MODEL_LIST', {
        list: data.list,
        total: data.total
      })
      return data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchModelProviders({ commit }, modelType) {
    try {
      const data = await getModelProviderList(modelType)
      commit('SET_MODEL_PROVIDERS', data)
      return data
    } catch (error) {
      silentError('获取模型供应器失败:', error)
      throw error
    }
  },

  async fetchModelConfig({ commit }, id) {
    try {
      const data = await getModelConfig(id)
      commit('SET_CURRENT_MODEL', data)
      return data
    } catch (error) {
      silentError('获取模型配置详情失败:', error)
      throw error
    }
  },

  async saveModelConfig({ dispatch }, { modelType, provideCode, id, data }) {
    try {
      let result
      if (id) {
        result = await editModelConfig(modelType, provideCode, id, data)
      } else {
        result = await addModelConfig(modelType, provideCode, data)
      }
      return result
    } catch (error) {
      silentError('保存模型配置失败:', error)
      throw error
    }
  },

  async deleteModelConfig({ dispatch }, id) {
    try {
      const result = await deleteModelConfig(id)
      return result
    } catch (error) {
      silentError('删除模型配置失败:', error)
      throw error
    }
  },

  async toggleModelStatus({ dispatch }, { id, status }) {
    try {
      const result = await enableModelConfig(id, status)
      return result
    } catch (error) {
      silentError('切换模型状态失败:', error)
      throw error
    }
  },

  async toggleActivationStatus({ dispatch }, { id, status }) {
    try {
      const result = await activateModelConfig(id, status)
      return result
    } catch (error) {
      silentError('切换模型开通状态失败:', error)
      throw error
    }
  },

  async setAsDefault({ dispatch }, id) {
    try {
      const result = await setDefaultModel(id)
      return result
    } catch (error) {
      silentError('设置默认模型失败:', error)
      throw error
    }
  },

  async fetchModelVoices({ commit }, { modelId, voiceName }) {
    try {
      const data = await getModelVoices(modelId, { voiceName })
      commit('SET_MODEL_VOICES', data)
      return data
    } catch (error) {
      silentError('获取模型音色失败:', error)
      throw error
    }
  },

  async fetchVoiceList({ commit }, params) {
    try {
      const data = await getVoiceList(params)
      return data
    } catch (error) {
      silentError('获取音色列表失败:', error)
      throw error
    }
  },

  async saveVoice({ dispatch }, data) {
    try {
      const result = await saveVoice(data)
      return result
    } catch (error) {
      silentError('保存音色失败:', error)
      throw error
    }
  },

  async updateVoice({ dispatch }, { id, data }) {
    try {
      const result = await updateVoice(id, data)
      return result
    } catch (error) {
      silentError('修改音色失败:', error)
      throw error
    }
  },

  async deleteVoices({ dispatch }, ids) {
    try {
      const result = await deleteVoice(ids)
      return result
    } catch (error) {
      silentError('删除音色失败:', error)
      throw error
    }
  },

  async fetchModelQuota({ dispatch }, { id, refresh = false }) {
    try {
      const data = await getModelQuota(id, refresh)
      return data
    } catch (error) {
      silentError('获取模型额度失败:', error)
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
